import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarCheckIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react'
import { useId, useState, type SyntheticEvent } from 'react'

import { ApiError } from '@/api/errors'
import { publicProspectingApi } from '@/api/prospecting'
import type { PublicLandingPage, PublicLandingResult } from '@/api/types'
import { FieldError, FormError } from '@/components/field-error'
import { Markdown } from '@/components/markdown'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { browserLocale, msg, setCurrentLocale } from '@/i18n'
import { attributionFrom } from '@/lib/prospecting'

/**
 * La page de qualification publique (lot 43, contrat 1.86.0) : sans
 * connexion, mobile d'abord, dans la langue du programme. Les questions une à
 * une, puis l'identité, la case de consentement — jamais cochée d'avance — et
 * le lien vers la politique ; le remerciement propose un rendez-vous si le
 * serveur le donne. La page ne dit jamais le score ni la classe.
 */
export const Route = createFileRoute('/p/$slug')({
  component: PublicLandingRoute,
})

function PublicLandingRoute() {
  const { slug } = Route.useParams()
  const page = useQuery({
    queryKey: ['public', 'prospecting', slug],
    queryFn: () => publicProspectingApi.page(slug),
    retry: false,
  })
  setCurrentLocale(page.data?.language ?? browserLocale())
  const t = msg().prospectingPublic

  return (
    <main className="min-h-dvh bg-muted/30 px-4 py-6 sm:py-12" data-testid="page-publique">
      <div className="mx-auto w-full max-w-lg space-y-5">
        {page.isPending ? (
          <Skeleton className="h-64 w-full" aria-label={t.loading} />
        ) : page.isError ? (
          <p className="rounded-lg border bg-card p-6 text-center" role="alert">
            {page.error instanceof ApiError && page.error.isNotFound ? t.missing : t.failed}
          </p>
        ) : (
          <Landing slug={slug} page={page.data} />
        )}
      </div>
    </main>
  )
}

type Answers = Record<string, string>

function Landing({ slug, page }: { slug: string; page: PublicLandingPage }) {
  const t = msg().prospectingPublic
  const ids = { consent: useId(), trap: useId() }
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Answers>({})
  const [identity, setIdentity] = useState<Record<string, string>>({})
  const [consent, setConsent] = useState(false)
  // Piège à robots : un champ que personne ne voit ; rempli, rien n'est enregistré.
  const [trap, setTrap] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [result, setResult] = useState<PublicLandingResult | null>(null)
  const total = page.questions.length + 1
  const question = page.questions[step]
  const fields = [
    'full_name',
    'email',
    ...page.identity_fields.filter((f) => f !== 'full_name' && f !== 'email'),
  ]

  const submit = useMutation({
    mutationFn: () =>
      publicProspectingApi.submit(slug, {
        full_name: (identity.full_name ?? '').trim(),
        email: (identity.email ?? '').trim(),
        ...Object.fromEntries(
          fields
            .filter((field) => field !== 'full_name' && field !== 'email')
            .map((field) => [field, (identity[field] ?? '').trim() || null]),
        ),
        answers: Object.entries(answers)
          .filter(([, value]) => value !== '')
          .map(([question_key, value]) => ({ question_key, value })),
        consent_messages: consent,
        attribution: attributionFrom(window.location.href, document.referrer),
        ...(trap ? { website_url: trap } : {}),
      }),
    onSuccess: (saved) => {
      setResult(saved)
    },
  })

  if (result) {
    return (
      <section
        className="space-y-4 rounded-lg border bg-card p-6 text-center"
        data-testid="remerciement"
      >
        <CheckCircle2Icon className="mx-auto size-10 text-success" aria-hidden="true" />
        <h1 className="text-xl font-semibold">{result.thanks_title}</h1>
        {result.thanks_body && <Markdown className="text-sm">{result.thanks_body}</Markdown>}
        {result.booking_url && (
          <Button asChild size="lg" className="w-full sm:w-auto">
            <a
              href={result.booking_url}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="prendre-rendez-vous"
            >
              <CalendarCheckIcon />
              {t.booking}
            </a>
          </Button>
        )}
      </section>
    )
  }

  const answer = question ? (answers[question.key] ?? '') : ''
  const next = (event: SyntheticEvent) => {
    event.preventDefault()
    if (question) {
      if (question.required && answer.trim() === '') {
        setProblem(question.type === 'multiple' ? t.pickSome : t.required)
        return
      }
      if (
        question.type === 'number' &&
        answer !== '' &&
        Number.isNaN(Number(answer.replace(',', '.')))
      ) {
        setProblem(t.number)
        return
      }
      setProblem(null)
      setStep(step + 1)
      return
    }
    if (!(identity.full_name ?? '').trim()) {
      setProblem(t.required)
      return
    }
    if (!/^\S+@\S+\.\S+$/.test((identity.email ?? '').trim())) {
      setProblem(t.invalidEmail)
      return
    }
    setProblem(null)
    submit.mutate()
  }

  const sendError =
    submit.error instanceof ApiError
      ? submit.error.status === 429
        ? t.quota
        : submit.error.reason === 'answer_required' || submit.error.reason === 'answer_invalid'
          ? t.answerProblem
          : t.sendFailed
      : submit.error
        ? t.sendFailed
        : null

  return (
    <form onSubmit={next} noValidate className="space-y-5" lang={page.language}>
      <header className="space-y-2">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          {page.issuer.logo_url && <img src={page.issuer.logo_url} alt="" className="h-6 w-auto" />}
          {page.issuer.name}
        </p>
        <h1 className="text-2xl font-semibold tracking-tight">{page.title}</h1>
        {step === 0 && page.intro && <Markdown className="text-sm">{page.intro}</Markdown>}
        <p className="text-xs text-muted-foreground" data-testid="etape">
          {t.step(step + 1, total)}
        </p>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
          <div
            className="h-full rounded-full bg-primary transition-[width]"
            style={{ width: `${String(((step + 1) / total) * 100)}%` }}
          />
        </div>
      </header>

      <section className="space-y-4 rounded-lg border bg-card p-5">
        {question ? (
          <QuestionStep
            question={question}
            value={answer}
            onChange={(value) => {
              setAnswers((current) => ({ ...current, [question.key]: value }))
            }}
          />
        ) : (
          <>
            <h2 className="font-medium">{t.identityTitle}</h2>
            {fields.map((field) => (
              <div key={field} className="space-y-1">
                <Label htmlFor={`${ids.consent}-${field}`}>
                  {t.fields[field as keyof typeof t.fields]}
                  {(field === 'full_name' || field === 'email') && ' *'}
                </Label>
                <Input
                  id={`${ids.consent}-${field}`}
                  type={
                    field === 'email'
                      ? 'email'
                      : field === 'phone'
                        ? 'tel'
                        : field === 'website'
                          ? 'url'
                          : 'text'
                  }
                  autoComplete={
                    field === 'full_name'
                      ? 'name'
                      : field === 'email'
                        ? 'email'
                        : field === 'phone'
                          ? 'tel'
                          : field === 'company'
                            ? 'organization'
                            : field === 'job_title'
                              ? 'organization-title'
                              : field === 'website'
                                ? 'url'
                                : 'address-level2'
                  }
                  value={identity[field] ?? ''}
                  onChange={(event) => {
                    setIdentity((current) => ({ ...current, [field]: event.target.value }))
                  }}
                />
              </div>
            ))}
            <div className="absolute left-[-10000px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor={ids.trap}>{t.honeypot}</label>
              <input
                id={ids.trap}
                name="website_url"
                tabIndex={-1}
                autoComplete="off"
                value={trap}
                onChange={(event) => {
                  setTrap(event.target.value)
                }}
              />
            </div>
            <div className="flex items-start gap-2 text-sm">
              <Checkbox
                id={ids.consent}
                className="mt-0.5"
                checked={consent}
                onCheckedChange={(checked) => {
                  setConsent(checked === true)
                }}
                data-testid="consentement-messages"
              />
              <Label htmlFor={ids.consent} className="font-normal">
                {page.consent_text}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t.privacyNote}{' '}
              <a
                href={page.privacy_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-primary underline-offset-2 hover:underline"
              >
                {t.privacy}
              </a>
            </p>
          </>
        )}
        {problem && <FieldError>{problem}</FieldError>}
        {sendError && <FormError>{sendError}</FormError>}
      </section>

      <div className="flex gap-2">
        {step > 0 && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={() => {
              setProblem(null)
              setStep(step - 1)
            }}
          >
            {t.back}
          </Button>
        )}
        <Button
          type="submit"
          size="lg"
          className="flex-1"
          disabled={submit.isPending}
          data-testid="etape-suivante"
        >
          {submit.isPending && <Loader2Icon className="animate-spin" />}
          {question ? t.next : submit.isPending ? t.sending : t.submit}
        </Button>
      </div>
    </form>
  )
}

function QuestionStep({
  question,
  value,
  onChange,
}: {
  question: PublicLandingPage['questions'][number]
  value: string
  onChange: (value: string) => void
}) {
  const id = useId()
  const chosen = value ? value.split(',') : []
  return (
    <fieldset className="space-y-3" data-testid="question-publique">
      <legend className="text-lg font-medium">
        {question.label}
        {question.required && ' *'}
      </legend>
      {question.type === 'single' &&
        question.options.map((option) => (
          <label
            key={option.key}
            htmlFor={`${id}-${option.key}`}
            className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-primary/5"
          >
            <input
              id={`${id}-${option.key}`}
              type="radio"
              name={id}
              className="accent-primary"
              checked={value === option.key}
              onChange={() => {
                onChange(option.key)
              }}
            />
            {option.label}
          </label>
        ))}
      {question.type === 'multiple' &&
        question.options.map((option) => (
          <label
            key={option.key}
            htmlFor={`${id}-${option.key}`}
            className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-primary/5"
          >
            <input
              id={`${id}-${option.key}`}
              type="checkbox"
              className="accent-primary"
              checked={chosen.includes(option.key)}
              onChange={(event) => {
                const next = event.target.checked
                  ? [...chosen, option.key]
                  : chosen.filter((key) => key !== option.key)
                onChange(next.join(','))
              }}
            />
            {option.label}
          </label>
        ))}
      {question.type === 'number' && (
        <Input
          aria-label={question.label}
          inputMode="decimal"
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
      )}
      {question.type === 'text' && (
        <Textarea
          aria-label={question.label}
          rows={4}
          maxLength={2000}
          value={value}
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
      )}
    </fieldset>
  )
}
